/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * QuestionComponent.web.tsx - Component for rendering the question and handling input
 */

import Numpad from './NumPad/Numpad';
import OddEvenPad from './NumPad/OddEvenPad';
import YesNoPad from './NumPad/YesNoPad';
import HintModal from './HintComponents/HintModal';
import { unreachable } from '../../utils/typescript';
import { isAdditiveSequenceQuestion, isEqualGroupsCountersQuestion, isEqualGroupsQuestion, isEquationQuestion, isOddOrEvenQuestion, isWordEquationQuestion, Question } from './types';
import { useCallback, useEffect, useState, type JSX } from 'react';
import QuestionStatement from './QuestionStatement/QuestionStatement';
import QuestionComponentProps from './QuestionComponentProps';
import './QuestionComponent.web.css';

// Web-only: stop enter button propogating to parents
function handleKeyboardDown(event: KeyboardEvent) {
  if (event.code === 'Enter' || event.code === 'NumpadEnter') {
    event.stopPropagation();
    event.preventDefault();
  }
}

/**
 * General component which can display a question to the user, and accept the user's input.
 * 
 * Is responsible for validating the user's answer and using the provided callback.
 * 
 * Does support being reused for a new question, since it is simple to reset this state.
 */
const QuestionComponent = ({ question, playSound, questionAnswered }: QuestionComponentProps) => {
  // Web-only: stop enter button propogating to parents
  useEffect(() => {
    document.addEventListener('keydown', handleKeyboardDown);
    return () => document.removeEventListener('keydown', handleKeyboardDown);
  }, []);

  // Hint visibility
  const [hintVisible, setHintVisible] = useState(false);
  const toggleHint = () => setHintVisible(wasVisible => !wasVisible);

  // Correctness - null represents neither correct nor incorrect (i.e. not answered yet)
  const [correct, setCorrect] = useState<boolean | null>(null);

  // User input
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [numpadInput, setNumpadInput] = useState(''); // For numpad use only

  // Reset state if the question changes
  useEffect(() => {
    setHintVisible(false);
    setCorrect(null);
    setButtonsDisabled(false);
    setNumpadInput('');
  }, [question]);

  const submitAnswer = useCallback((userAnswer?: Question['answer']) => {
    setButtonsDisabled(true);
    
    const isCorrect = userAnswer === question.answer;
    setCorrect(isCorrect);
    playSound(isCorrect);

    questionAnswered(isCorrect, userAnswer);
  }, [question, playSound, questionAnswered]);

  const tickOrCross = ((): JSX.Element | null => {
    switch (correct) {
      case true:
        return <p id="tick">✓</p>;
      case false:
        return <p id="cross">✗</p>;
      case null:
        return null;
    }
  })();

  // Callbacks for input types (need to be defined unconditionally since they are hooks)
  const handleNumpadInput = useCallback((input: string) => setNumpadInput(old => old + input), []);
  const handleNumpadBackspace = useCallback(() => setNumpadInput(old => old.slice(0, -1)), []);
  const handleNumpadConfirm = useCallback(
    () => numpadInput ? submitAnswer(parseInt(numpadInput)) : submitAnswer(),
    [numpadInput, submitAnswer]
  );
  const handleYesNoInput = useCallback((input: 'Yes' | 'No') => submitAnswer(input), [submitAnswer]);
  const handleOddEvenInput = useCallback((input: 'Odd' | 'Even') => submitAnswer(input), [submitAnswer]);

  // Get the statement and input elements depending on the question type.
  let statementAndInput: JSX.Element;

  if (isEquationQuestion(question) || isWordEquationQuestion(question) || isAdditiveSequenceQuestion(question)) {
    // These question types use a numpad.
    statementAndInput = (<>
      <div id="questions_question_view">
        {tickOrCross}
        <QuestionStatement question={question} userAnswer={numpadInput} />
      </div>
      <div id="questions_pad_view">
        <Numpad
          handleInputEvent={handleNumpadInput}
          handleBackspaceEvent={handleNumpadBackspace}
          handleConfirmEvent={handleNumpadConfirm}
          disabled={buttonsDisabled}
        />
      </div>
    </>);

  } else if (isEqualGroupsQuestion(question) || isEqualGroupsCountersQuestion(question)) {
    // This question type uses a YesNoPad
    statementAndInput = (<>
      <div id="multichoice_question_view">
        {tickOrCross}
        <QuestionStatement question={question} userAnswer={numpadInput} />
      </div>
      <div id="multichoice_pad_view">
        <YesNoPad
          handleInputEvent={handleYesNoInput}
          disabled={buttonsDisabled}
        />
      </div>
    </>);

  } else if (isOddOrEvenQuestion(question)) {
    // This question type uses an OddEvenPad
    statementAndInput = (<>
      <div id="multichoice_question_view">
        {tickOrCross}
        <QuestionStatement question={question} userAnswer={numpadInput} />
      </div>
      <div id="multichoice_pad_view">
        <OddEvenPad
          handleInputEvent={handleOddEvenInput}
          disabled={buttonsDisabled}
        />
      </div>
    </>);

  } else {
    unreachable(question);
  }

  return (
    <div id="questions_wrapper">
      <div id="questions_content">
        <HintModal
          question={question}
          hintVisible={hintVisible}
          toggleHint={toggleHint}/>
        {statementAndInput}
        <div id="questions_hint_button_view">
          <button
            id="questions_hint_button"
            onClick={toggleHint}>
            <p id="questions_hint_button_text">Show Hint</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionComponent;
